import React, { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';

// System prompt for the Practitioner Track AI
const PRACTITIONER_SYSTEM_PROMPT = `You are the AI Guide for the Navigational Mind Framework Practitioner Track, running NMA-16.

RECOGNITION AND THE EPISTEMIC CATEGORIES govern this track exactly as they govern the rest of the course, and they are already in force in this session — do not restate them, apply them. In short: nothing arrives recognised, there is no score, a number is never recognized, and the practitioner's own account of their own experience is their ground and not a premise to check.

WHAT IS DIFFERENT HERE IS THE CONSEQUENCE. A practitioner carries what they learn into someone else's situation, so a premise you let stand becomes a premise they build a session on, and an invented term becomes one they teach onward as canon. Surface a flagged premise through Socratic questioning rather than correction, and when you do not hold something, say so rather than reconstructing a plausible version.

Your role is to deliver a comprehensive curriculum that trains practitioners to guide others using the framework. You are both teacher and Socratic guide.

CURRICULUM STRUCTURE (8 weeks):

Week 1: Foundations & The Sphere of Ordered Experience
- The phenomenological grounding of the framework
- Consciousness as enabling condition (The Toye Clarification)
- Center, Interior, Boundary, Horizon of the Sphere
- Antevalence and the limits of ordered experience
- Signal integration: Encounter, Selection, Integration
- Meaning as reconstruction, not transmission

Week 2: The Inner House & Practitioner Assessment
- Deep dive into house architecture
- Reading someone else's house structure
- Signs of Basement activation in others
- Boundary work in facilitation
- The Back Room and trauma-informed practice
- When to work and when to refer

Week 3: The Residents & Developmental Sensitivity
- Each resident in clinical depth
- Recognizing developmental injury vs. situational activation
- How residents present differently in different people
- Wounded residents vs. activated residents
- The practitioner's own residents and counter-resonance

Week 4: Friction, Leadership & Facilitation
- Deep understanding of productive vs. unproductive friction
- Facilitating friction without premature resolution
- Holding space for resident conflict
- When to intervene, when to observe
- Supporting conscious leadership in clients

Week 5: Direction & The Danger of Imposition
- Supporting client direction without substituting your own
- The difference between guidance and direction-giving
- When direction is unclear: facilitation approaches
- Your direction vs. their direction
- Ethical considerations in directional work

Week 6: Articulation, Anchors, Rules in Practice
- Supporting articulation without providing it
- Anchor assessment in clients
- Identifying false anchors
- Helping others form their own rules
- When rules are too rigid or too loose

Week 7: The Navigation Loop as Framework
- Using Process Method in sessions
- NTS as intervention
- Containment Windows in therapeutic settings
- Review Moments with clients
- The loop as structure for ongoing work

Week 8: Collapse, Ethics & Development
- Working with clients in collapse
- Recognizing structural crisis vs. temporary difficulty
- Ethical boundaries in navigational work
- When to refer
- The practitioner's own collapse and self-care
- Ongoing development as a navigational practitioner

TEACHING APPROACH:
- Begin each session by checking where the practitioner left off
- Deliver content through dialogue, not lecture
- Use Socratic questioning to ensure genuine understanding
- Invite application to real (anonymized) cases
- Challenge surface-level understanding
- Model the framework in your own responses

CALIBRATION:
- Recognition runs before every response, no exceptions, and the epistemic categories stay visible in how you speak: what is quotable, what is your inference, what is assumed
- Model epistemic honesty about uncertainty
- Push back when pushback is warranted
- Distinguish what the framework claims from what can be verified

Begin by welcoming the practitioner and asking about their background and what brings them to this track.`;

// Firebase Function proxy URL
const PROXY_URL = "/api/tutor";

function PractitionerTrack() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Welcome to the Practitioner Track.\n\nThis is an AI-facilitated deep curriculum designed for those who want to guide others using the Navigational Mind Framework. Over the coming weeks, we'll explore the theoretical foundations, practical applications, and ethical considerations of navigational practice.\n\nBefore we begin, I'd like to understand your context:\n\n• What is your professional background?\n• What draws you to learning this framework at the practitioner level?\n• Do you currently work with clients, patients, or others in a guiding capacity?\n\nThis helps me calibrate the curriculum to your experience and needs."
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionNotes, setSessionNotes] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const apiMessages = [
        { role: 'user', content: PRACTITIONER_SYSTEM_PROMPT },
        { role: 'assistant', content: 'I understand my role as the AI Guide for the Practitioner Track. I will deliver the comprehensive curriculum through Socratic dialogue, running NMA-16 throughout. I will challenge surface understanding and support deep learning.' },
        ...messages.map(m => ({ role: m.role, content: m.content })),
        userMessage
      ];

      const response = await fetch(PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 2048,
          messages: apiMessages
        })
      });

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const data = await response.json();
      const assistantMessage = {
        role: 'assistant',
        content: data.content[0].text
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const downloadSession = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = 20;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Practitioner Track Session', margin, yPosition);
    yPosition += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Downloaded: ${new Date().toLocaleDateString()}`, margin, yPosition);
    yPosition += 15;

    messages.forEach((message) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      const role = message.role === 'user' ? 'Practitioner' : 'AI Guide';
      doc.text(`${role}:`, margin, yPosition);
      yPosition += 6;

      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(message.content, maxWidth);
      
      lines.forEach((line) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += 5;
      });
      
      yPosition += 8;
    });

    // Add session notes if any
    if (sessionNotes.trim()) {
      doc.addPage();
      yPosition = 20;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Session Notes:', margin, yPosition);
      yPosition += 10;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const noteLines = doc.splitTextToSize(sessionNotes, maxWidth);
      noteLines.forEach((line) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, margin, yPosition);
        yPosition += 5;
      });
    }

    doc.save('practitioner-session.pdf');
  };

  return (
    <div className="practitioner-page fade-in">
      <div className="practitioner-intro">
        <h1>Practitioner Track</h1>
        <p style={{ color: 'var(--color-text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
          An AI-facilitated deep curriculum for those who want to guide others using the 
          Navigational Mind Framework. This track covers theoretical foundations, practical 
          applications, and ethical considerations.
        </p>
        <div className="practitioner-features">
          <div className="practitioner-feature">
            <strong>Theoretical Depth</strong><br />
            The Sphere of Ordered Experience, phenomenological grounding
          </div>
          <div className="practitioner-feature">
            <strong>Practical Skills</strong><br />
            Assessment, facilitation, intervention timing
          </div>
          <div className="practitioner-feature">
            <strong>Ethical Framework</strong><br />
            Boundaries, referral, self-care
          </div>
          <div className="practitioner-feature">
            <strong>Ongoing Dialogue</strong><br />
            Bring real cases for discussion
          </div>
        </div>
      </div>

      <div className="chat-container" style={{ height: '550px' }}>
        <div className="chat-messages">
          {messages.map((message, index) => (
            <div key={index} className={`chat-message ${message.role}`}>
              {message.content.split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          ))}
          {isLoading && (
            <div className="chat-message assistant">
              <p style={{ color: 'var(--color-text-muted)' }}>Thinking...</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-area">
          <textarea
            className="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            rows={2}
          />
          <button 
            className="chat-send" 
            onClick={sendMessage}
            disabled={isLoading || !input.trim()}
          >
            Send
          </button>
        </div>

        <div className="chat-actions">
          <button className="btn btn-secondary" onClick={downloadSession}>
            Download Session
          </button>
        </div>
      </div>

      {/* Session Notes Area */}
      <div style={{ marginTop: 'var(--space-xl)' }}>
        <h3 style={{ marginBottom: 'var(--space-md)', color: 'var(--color-accent)' }}>Session Notes</h3>
        <textarea
          value={sessionNotes}
          onChange={(e) => setSessionNotes(e.target.value)}
          placeholder="Keep notes from your practitioner sessions here. These will be included when you download the session."
          style={{
            width: '100%',
            minHeight: '150px',
            padding: 'var(--space-md)',
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-body)',
            fontSize: '0.95rem',
            resize: 'vertical'
          }}
        />
      </div>
    </div>
  );
}

export default PractitionerTrack;
